import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { ProductEventDataList } from 'corgi-types';
import type { BackgroundColorProps, BorderRadiusProps, WidthProps } from 'styled-system';
import { backgroundColor, borderRadius, width } from 'styled-system';

import { CssGrid } from 'common/components/CssGrid';
import ProductCard from 'common/components/ProductCard';
import { useObserveArticles } from 'common/tracking/useObserveArticles';
import type { Product } from 'common/types/product-card';

interface ProductCardListProps {
  articles: Product[];
  productEventDataListType: ProductEventDataList;
  columnsCount?: number;
}

const containerStyles = (columnsCount: number): ComponentProps<typeof CssGrid> => ({
  mb: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
  gridGap: ['sp_4', 'sp_8', 'sp_12', 'sp_16'],
  gridTemplateColumns: [
    'repeat(2, minmax(0, 1fr))',
    'repeat(3, minmax(0, 1fr))',
    `repeat(${columnsCount}, minmax(0, 1fr))`,
  ],
  width: 1,
});

const ListItem = styled.article<WidthProps & BackgroundColorProps & BorderRadiusProps>`
  ${width};
  ${backgroundColor};
  ${borderRadius};
`;

const listItemStyles: ComponentProps<typeof ListItem> = {
  backgroundColor: 'sys.neutral.background.default',
  borderRadius: 'medium',
  width: '100%',
};

const ProductCardList = ({
  articles,
  columnsCount = 3,
  productEventDataListType: listType,
}: ProductCardListProps) => {
  useObserveArticles({ articles, listType });

  return (
    <CssGrid {...containerStyles(columnsCount)} data-testid="list-view-inner-container">
      {articles.map((article, index) => (
        <ListItem
          key={article.sku}
          {...listItemStyles}
          data-testid="product-item"
          data-sku={article.sku}
        >
          <ProductCard
            article={article}
            index={index}
            productEventDataListType={listType}
            withProminentPricing
          />
        </ListItem>
      ))}
    </CssGrid>
  );
};

export default ProductCardList;
